import React, { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { ImagePlus, X } from "lucide-react";

import Button from "../../../../../components/buttons/Button";
import TextField from "../../../../../components/input/textfield/TextField";
import TextArea from "../../../../../components/input/textarea/TextArea";
import Select from "../../../../../components/input/select/Select";

import { compressImageFile } from "../../../../../utils/helpers/compression/imageCompressor";
import { uploadThumbnailToS3 } from "../../../../../utils/helpers/s3Uploader/uploadThumbnailToS3";

import * as StreamService from "../../../creators/services/creatorStreamsService";

import "./CreatorEditStreamForm.css";

const STREAM_TYPE_OPTIONS = [
  { label: "Match", value: "match" },
  { label: "Training", value: "training" },
  { label: "Event", value: "event" },
];

const ACCESS_TYPE_OPTIONS = [
  { label: "Public", value: "public" },
  { label: "Unlisted", value: "unlisted" },
  { label: "Group", value: "group" },
  { label: "Private", value: "private" },
];

const createInitialFormData = () => ({
  title: "",
  description: "",
  streamType: "match",
  accessType: "public",
  scheduledStart: "",
  thumbnailUrl: "",
  thumbnailFile: null,
  thumbnailPreview: "",
  recordingEnabled: false,
});

const formatDateTimeLocal = (value) => {
  if (!value) return "";

  const date = new Date(value);

  if (Number.isNaN(date.getTime())) return "";

  const offset = date.getTimezoneOffset() * 60 * 1000;

  return new Date(date.getTime() - offset).toISOString().slice(0, 16);
};

const getErrorMessage = (error, fallback) =>
  error?.response?.data?.message || error?.message || fallback;

const getAccessNote = (accessType) => {
  switch (accessType) {
    case "unlisted":
      return "Only people with access to this stream can view it.";

    case "group":
      return "Group access is managed in Shared Groups. Add at least one permitted group.";

    case "private":
      return "Only you can access this stream.";

    default:
      return "This stream is publicly available.";
  }
};

export default function CreatorEditStreamForm({
  stream,
  loading = false,
  error,
  onSave,
  onClose,
  onCancel,
}) {
  const [formData, setFormData] = useState(createInitialFormData);
  const [formError, setFormError] = useState("");
  const [saving, setSaving] = useState(false);
  const [thumbnailUploading, setThumbnailUploading] = useState(false);
  const [thumbnailProgress, setThumbnailProgress] = useState(0);

  const thumbnailInputRef = useRef(null);
  const thumbnailPreviewRef = useRef("");
  const savingRef = useRef(false);
  const mountedRef = useRef(true);

  const closeForm = onClose || onCancel;

  const isScheduled =
    String(stream?.status || "").toLowerCase() === "scheduled";

  const isBusy = loading || saving || thumbnailUploading;

  const title = formData.title.trim();
  const description = formData.description.trim();

  const currentThumbnail =
    formData.thumbnailPreview || formData.thumbnailUrl || "";

  const validStreamType = STREAM_TYPE_OPTIONS.some(
    (option) => option.value === formData.streamType,
  );

  const validAccessType = ACCESS_TYPE_OPTIONS.some(
    (option) => option.value === formData.accessType,
  );

  const canSubmit =
    Boolean(stream) &&
    Boolean(title) &&
    validStreamType &&
    validAccessType &&
    typeof onSave === "function" &&
    !isBusy;

  const errorMessage =
    formError ||
    (typeof error === "string"
      ? error
      : error
        ? getErrorMessage(error, "")
        : "");

  const progress = Math.min(
    100,
    Math.max(0, Math.round(Number(thumbnailProgress) || 0)),
  );

  const submitLabel = thumbnailUploading
    ? `Uploading ${progress}%`
    : isBusy
      ? "Saving..."
      : "Save Changes";

  useEffect(() => {
    mountedRef.current = true;

    return () => {
      mountedRef.current = false;

      if (thumbnailPreviewRef.current) {
        URL.revokeObjectURL(thumbnailPreviewRef.current);
        thumbnailPreviewRef.current = "";
      }
    };
  }, []);

  useEffect(() => {
    if (thumbnailPreviewRef.current) {
      URL.revokeObjectURL(thumbnailPreviewRef.current);
      thumbnailPreviewRef.current = "";
    }

    if (!stream) {
      setFormData(createInitialFormData());
      setFormError("");
      return;
    }

    setFormData({
      title: String(stream.title || ""),
      description: String(stream.description || ""),
      streamType: stream.streamType || stream.stream_type || "match",
      accessType: stream.accessType || stream.access_type || "public",
      scheduledStart: formatDateTimeLocal(
        stream.scheduledStart || stream.scheduled_start,
      ),
      thumbnailUrl: stream.thumbnailUrl || stream.thumbnail_url || "",
      thumbnailFile: null,
      thumbnailPreview: "",
      recordingEnabled:
        Number(stream.recordingEnabled ?? stream.recording_enabled) === 1 ||
        stream.recordingEnabled === true ||
        stream.recording_enabled === true,
    });

    setFormError("");
    setThumbnailProgress(0);
  }, [stream]);

  useEffect(() => {
    const handleEscape = (event) => {
      if (event.key === "Escape" && !isBusy && !savingRef.current) {
        event.preventDefault();
        closeForm?.();
      }
    };

    document.addEventListener("keydown", handleEscape);

    return () => {
      document.removeEventListener("keydown", handleEscape);
    };
  }, [isBusy, closeForm]);

  const handleInputChange = (event) => {
    const { name, value, type, checked } = event.target;

    setFormData((previous) => ({
      ...previous,
      [name]: type === "checkbox" ? checked : value,
    }));

    setFormError("");
  };

  const handleThumbnailChange = (event) => {
    const file = event.target.files?.[0];

    event.target.value = "";

    if (!file || isBusy || savingRef.current) return;

    setFormError("");

    if (!["image/jpeg", "image/png", "image/webp"].includes(file.type)) {
      setFormError("Please select a JPG, PNG or WebP image.");
      return;
    }

    const preview = URL.createObjectURL(file);
    const previousPreview = thumbnailPreviewRef.current;

    thumbnailPreviewRef.current = preview;

    setFormData((previous) => ({
      ...previous,
      thumbnailFile: file,
      thumbnailPreview: preview,
    }));

    if (previousPreview) {
      URL.revokeObjectURL(previousPreview);
    }

    setThumbnailProgress(0);
  };

  const handleRemoveThumbnail = () => {
    if (isBusy || savingRef.current) return;

    if (thumbnailPreviewRef.current) {
      URL.revokeObjectURL(thumbnailPreviewRef.current);
      thumbnailPreviewRef.current = "";
    }

    setFormData((previous) => ({
      ...previous,
      thumbnailUrl: "",
      thumbnailFile: null,
      thumbnailPreview: "",
    }));

    if (thumbnailInputRef.current) {
      thumbnailInputRef.current.value = "";
    }

    setThumbnailProgress(0);
    setFormError("");
  };

  const handleClose = () => {
    if (isBusy || savingRef.current) return;
    closeForm?.();
  };

  const uploadThumbnail = async (file, existingUrl) => {
    if (!file) return existingUrl || null;

    setThumbnailUploading(true);
    setThumbnailProgress(0);

    const compressedFile = await compressImageFile({
      file,
      maxWidth: 1280,
      maxHeight: 720,
      quality: 0.72,
      outputType: "image/jpeg",
    });

    const response = await StreamService.createStreamThumbnailUploadUrl(
      compressedFile.type,
    );

    const { uploadUrl, thumbnailUrl } = response?.data || {};

    if (!uploadUrl || !thumbnailUrl) {
      throw new Error("Unable to prepare thumbnail upload.");
    }

    await uploadThumbnailToS3({
      uploadUrl,
      file: compressedFile,
      onProgress: (value) => {
        if (mountedRef.current) {
          setThumbnailProgress(value);
        }
      },
    });

    if (mountedRef.current) {
      setThumbnailProgress(100);
    }

    return thumbnailUrl;
  };

  const handleSubmit = async (event) => {
    event?.preventDefault?.();

    if (isBusy || savingRef.current) return;

    if (!stream) {
      setFormError("Stream information is missing.");
      return;
    }

    if (typeof onSave !== "function") {
      setFormError("Stream update handler is unavailable.");
      return;
    }

    if (!title) {
      setFormError("Stream title is required.");
      return;
    }

    if (title.length > 120) {
      setFormError("Stream title cannot exceed 120 characters.");
      return;
    }

    if (!validStreamType || !validAccessType) {
      setFormError("Please select valid stream settings.");
      return;
    }

    savingRef.current = true;
    setSaving(true);
    setFormError("");

    try {
      const thumbnailUrl = await uploadThumbnail(
        formData.thumbnailFile,
        formData.thumbnailUrl,
      );

      const payload = {
        title,
        description,
        streamType: formData.streamType,
        accessType: formData.accessType,
        scheduledStart: formData.scheduledStart || null,
        thumbnailUrl,
      };

      if (isScheduled) {
        payload.recordingEnabled = formData.recordingEnabled ? 1 : 0;
      }

      const result = await onSave(payload);

      if (result === false || result?.success === false) {
        throw new Error(result?.message || "Unable to update stream details.");
      }

      closeForm?.();
    } catch (submitError) {
      console.error("Unable to update stream:", submitError);

      if (mountedRef.current) {
        setFormError(
          getErrorMessage(submitError, "Unable to update stream details."),
        );
      }
    } finally {
      savingRef.current = false;

      if (mountedRef.current) {
        setThumbnailUploading(false);
        setSaving(false);
      }
    }
  };

  return createPortal(
    <form
      className="creator-edit-stream-form"
      onSubmit={handleSubmit}
      aria-label="Edit Stream"
      aria-busy={isBusy}
    >
      <header className="creator-edit-stream-form__header">
        <div className="creator-edit-stream-form__header-info">
          <h2>Edit Stream</h2>
          <p>Update your stream information and settings.</p>
        </div>

        <button
          type="button"
          className="creator-edit-stream-form__close"
          onClick={handleClose}
          disabled={isBusy}
          aria-label="Close stream editor"
        >
          <X size={20} />
        </button>
      </header>

      <div className="creator-edit-stream-form__workspace">
        <main className="creator-edit-stream-form__main">
          <div className="creator-edit-stream-form__section-header">
            <h3>Stream Information</h3>
            <p>Update the details displayed to viewers.</p>
          </div>

          <div className="creator-edit-stream-form__fields">
            <TextField
              label="Stream Title"
              name="title"
              value={formData.title}
              onChange={handleInputChange}
              placeholder="Bucks vs Harlequins"
              maxLength={120}
              required
              disabled={isBusy || !stream}
            />

            <TextArea
              label="Description"
              name="description"
              value={formData.description}
              onChange={handleInputChange}
              placeholder="Add information about the stream"
              maxLength={1000}
              maxHeight={180}
              disabled={isBusy || !stream}
            />
          </div>

          <section className="creator-edit-stream-form__thumbnail">
            <div className="creator-edit-stream-form__section-header">
              <h3>Stream Thumbnail</h3>
              <p>Optional · Recommended 1280 × 720</p>
            </div>

            {currentThumbnail ? (
              <div className="creator-edit-stream-form__preview">
                <img src={currentThumbnail} alt="Stream thumbnail preview" />

                <button
                  type="button"
                  className="creator-edit-stream-form__remove-thumbnail"
                  onClick={handleRemoveThumbnail}
                  disabled={isBusy}
                  aria-label="Remove thumbnail"
                >
                  <X size={18} />
                </button>
              </div>
            ) : null}

            <label className="creator-edit-stream-form__thumbnail-picker">
              <ImagePlus size={22} strokeWidth={1.5} />

              <span>
                {currentThumbnail ? "Replace Thumbnail" : "Add Thumbnail"}
              </span>

              <input
                ref={thumbnailInputRef}
                type="file"
                accept="image/jpeg,image/png,image/webp"
                onChange={handleThumbnailChange}
                disabled={isBusy || !stream}
                hidden
              />
            </label>

            {thumbnailUploading && (
              <div
                className="creator-edit-stream-form__progress"
                role="status"
                aria-live="polite"
              >
                <div className="creator-edit-stream-form__progress-header">
                  <span>Uploading thumbnail</span>
                  <strong>{progress}%</strong>
                </div>

                <div
                  className="creator-edit-stream-form__progress-track"
                  role="progressbar"
                  aria-label="Thumbnail upload progress"
                  aria-valuemin={0}
                  aria-valuemax={100}
                  aria-valuenow={progress}
                >
                  <div
                    className="creator-edit-stream-form__progress-fill"
                    style={{ width: `${progress}%` }}
                  />
                </div>
              </div>
            )}
          </section>
        </main>

        <aside className="creator-edit-stream-form__sidebar">
          <div className="creator-edit-stream-form__section-header">
            <h3>Stream Settings</h3>
            <p>Manage your broadcast and access options.</p>
          </div>

          <div className="creator-edit-stream-form__fields">
            <Select
              label="Stream Type"
              name="streamType"
              value={formData.streamType}
              onChange={handleInputChange}
              items={STREAM_TYPE_OPTIONS}
              required
              disabled={isBusy || !stream}
            />

            <Select
              label="Access Type"
              name="accessType"
              value={formData.accessType}
              onChange={handleInputChange}
              items={ACCESS_TYPE_OPTIONS}
              required
              disabled={isBusy || !stream}
            />

            <p className="creator-edit-stream-form__help">
              {getAccessNote(formData.accessType)}
            </p>

            <TextField
              label="Scheduled Start"
              type="datetime-local"
              name="scheduledStart"
              value={formData.scheduledStart}
              onChange={handleInputChange}
              disabled={isBusy || !stream}
            />

            {isScheduled ? (
              <label className="creator-edit-stream-form__checkbox">
                <input
                  type="checkbox"
                  name="recordingEnabled"
                  checked={formData.recordingEnabled}
                  onChange={handleInputChange}
                  disabled={isBusy}
                />

                <span>Enable recording after the live stream</span>
              </label>
            ) : (
              <p className="creator-edit-stream-form__help">
                Recording settings are locked after the stream starts.
              </p>
            )}
          </div>

          {stream && (
            <div className="creator-edit-stream-form__summary">
              <h4>Stream Summary</h4>

              <div>
                <span>Status</span>
                <strong>{stream.status || "—"}</strong>
              </div>

              <div>
                <span>Type</span>
                <strong>{formData.streamType}</strong>
              </div>

              <div>
                <span>Access</span>
                <strong>{formData.accessType}</strong>
              </div>

              <div>
                <span>Recording</span>
                <strong>
                  {formData.recordingEnabled ? "Enabled" : "Disabled"}
                </strong>
              </div>

              <div>
                <span>Thumbnail</span>
                <strong>{currentThumbnail ? "Added" : "Not added"}</strong>
              </div>
            </div>
          )}
        </aside>
      </div>

      <footer className="creator-edit-stream-form__footer">
        <div className="creator-edit-stream-form__footer-status">
          {errorMessage ? (
            <span className="creator-edit-stream-form__error" role="alert">
              {errorMessage}
            </span>
          ) : thumbnailUploading ? (
            <span>Uploading thumbnail · {progress}%</span>
          ) : saving || loading ? (
            <span>Saving stream changes...</span>
          ) : !stream ? (
            <span>Stream information is unavailable.</span>
          ) : null}
        </div>

        <div className="creator-edit-stream-form__actions">
          <Button
            type="button"
            value="Cancel"
            variant="primary-outlined"
            onClick={handleClose}
            disabled={isBusy}
          />

          <Button
            type="submit"
            value={submitLabel}
            variant="primary"
            disabled={!canSubmit}
          />
        </div>
      </footer>
    </form>,
    document.body,
  );
}
